import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { useEffect, useState, useRef } from 'react';
import { auth } from '../core/firebaseConfig.ts';
import axiosInstance from '../core/axiosConfig.ts';
import * as Sentry from '@sentry/react';

const MIN_RECONNECT_DELAY = 5000;
const MAX_RECONNECT_DELAY = 30000;
const POLL_FALLBACK_DELAY = 60000;
const POLL_INTERVAL = 15000;
const HOOK_NAME = 'useOrdersListVendor';

export const useOrdersListVendor = (locationId: number) => {
    const [orders, setOrders] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState(null);
    const stompClient = useRef(null);
    const pollTimeout = useRef(null);
    const pollInterval = useRef(null);
    const disconnectedAt = useRef(null);

    useEffect(() => {
        if (stompClient.current && stompClient.current.connected) {
            console.error('Connection already exists!');
            return;
        }

        const stopPolling = () => {
            if (pollTimeout.current) {
                clearTimeout(pollTimeout.current);
                pollTimeout.current = null;
            }
            if (pollInterval.current) {
                clearInterval(pollInterval.current);
                pollInterval.current = null;
            }
        };

        const pollOrders = async () => {
            try {
                const { data } = await axiosInstance.get('/order/list/active');
                setOrders(data);
                Sentry.addBreadcrumb({
                    category: 'websocket',
                    message: 'REST poll fallback succeeded',
                    level: 'info',
                    data: { hook: HOOK_NAME, locationId, count: data?.length },
                });
            } catch (err) {
                console.error('Polling fallback failed:', err);
                Sentry.captureException(err, { tags: { hook: HOOK_NAME, locationId } });
            }
        };

        // WS can stay disconnected far longer than the reconnect backoff (e.g. real
        // network outages), so once we've been down for a while we fall back to REST
        // polling to keep getting order updates without relying on the socket at all.
        const scheduleFallback = () => {
            if (!pollTimeout.current && !pollInterval.current) {
                pollTimeout.current = setTimeout(() => {
                    Sentry.captureMessage('WebSocket down 60s+, falling back to REST polling', {
                        level: 'warning',
                        tags: { hook: HOOK_NAME, locationId },
                    });
                    pollOrders();
                    pollInterval.current = setInterval(pollOrders, POLL_INTERVAL);
                }, POLL_FALLBACK_DELAY);
            }
        };

        const connect = async () => {
            const token = await auth.currentUser?.getIdToken();

            const client = new Client({
                webSocketFactory: () => new SockJS(`${process.env.REACT_APP_API_URL}/ws`),
                connectHeaders: {
                    Authorization: `Bearer ${token}`,
                },

                onConnect: (frame) => {
                    setIsConnected(true);
                    setError(null);
                    client.reconnectDelay = MIN_RECONNECT_DELAY;
                    stopPolling();

                    if (disconnectedAt.current) {
                        Sentry.captureMessage('WebSocket reconnected after downtime', {
                            level: 'warning',
                            tags: { hook: HOOK_NAME, locationId },
                            extra: { downtimeMs: Date.now() - disconnectedAt.current },
                        });
                        disconnectedAt.current = null;
                    }

                    Sentry.addBreadcrumb({
                        category: 'websocket',
                        message: 'STOMP connected',
                        level: 'info',
                        data: { hook: HOOK_NAME, locationId },
                    });

                    console.log(`📡 Subscribing to: /topic/orders/list/${locationId}`);

                    client.subscribe(`/topic/orders/list/${locationId}`, (message) => {
                        try {
                            const ordersList = JSON.parse(message.body);
                            setOrders(ordersList);
                        } catch (err) {
                            console.error('Error parsing message:', err);
                            setError('Failed to parse orders');
                            Sentry.captureException(err, { tags: { hook: HOOK_NAME, locationId } });
                        }
                    });

                    console.log('📡 Requesting current orders list...');
                    client.publish({
                        destination: '/app/orders/list/request',
                        body: ''
                    });
                },

                onDisconnect: () => {
                    console.log('Websocket disconnected');
                    setIsConnected(false);
                    Sentry.addBreadcrumb({
                        category: 'websocket',
                        message: 'STOMP disconnected',
                        level: 'warning',
                        data: { hook: HOOK_NAME, locationId },
                    });
                },

                onStompError: (frame) => {
                    const message = frame.headers['message'] || 'Connection error';
                    setError(message);
                    setIsConnected(false);
                    Sentry.captureMessage('STOMP protocol error', {
                        level: 'error',
                        tags: { hook: HOOK_NAME, locationId },
                        extra: { message, body: frame.body },
                    });
                },

                onWebSocketError: (event) => {
                    console.error('⚠WebSocket error:', event);
                    setError('WebSocket connection failed');
                    Sentry.addBreadcrumb({
                        category: 'websocket',
                        message: 'WebSocket error event',
                        level: 'error',
                        data: { hook: HOOK_NAME, locationId },
                    });
                },

                onWebSocketClose: (event) => {
                    console.log('WebSocket closed, will keep retrying');
                    setIsConnected(false);

                    if (!disconnectedAt.current) {
                        disconnectedAt.current = Date.now();
                    }

                    client.reconnectDelay = Math.min(client.reconnectDelay * 1.5, MAX_RECONNECT_DELAY);

                    Sentry.addBreadcrumb({
                        category: 'websocket',
                        message: 'WebSocket closed',
                        level: 'warning',
                        data: {
                            hook: HOOK_NAME,
                            locationId,
                            code: event?.code,
                            reason: event?.reason,
                            nextReconnectDelayMs: client.reconnectDelay,
                        },
                    });

                    scheduleFallback();
                },

                beforeConnect: async () => {
                    const freshToken = await auth.currentUser?.getIdToken();
                    client.connectHeaders = { Authorization: `Bearer ${freshToken}` };
                },

                reconnectDelay: MIN_RECONNECT_DELAY,
                heartbeatIncoming: 4000,
                heartbeatOutgoing: 4000,
            });

            client.activate();
            stompClient.current = client;
        };

        connect();

        return () => {
            stopPolling();
            if (stompClient.current) {
                stompClient.current.deactivate();
                stompClient.current = null;
            }
        };
    }, []);

    return { orders, isConnected, error };
};
